import React, { useState, useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { isEmpty, merge, get as lodashGet } from "lodash";
import { v4 as uuidv4 } from "uuid";

import {
	BarOrientation,
	ChartColors,
	ChartFonts,
	IChartThemes,
	ICategoryAxisConfig,
	IValueAxisConfig,
	ISeriesConfig,
	XYChartType,
	IXYChartProps,
	IChartConfig,
	IDataEntries,
	IChartLegend,
	IChartCursor,
} from "./XYChart.d";

am4core.useTheme(am4themes_animated);

const XYChart = (props: IXYChartProps) => {
	const [chartId, updateChartId] = useState("XY_CHART_" + uuidv4());
	const [noDataFlag, updateNoDataFlag] = useState(false);
	const [chartHeight, updateChartHeight] = useState("0");
	const [chartMinWidth, updateChartMinWidth] = useState(100);
	let _chartInstance: any;

	useEffect(() => {
		if (props.config) {
			createChart(props.config);
		}
		return () => {
			if (_chartInstance) {
				_chartInstance.dispose();
			}
		};
	}, [props.config]);

	const createChart = (config: IChartConfig) => {
		const {
			modifiedData,
			barOrientation,
			tooltipHTMLArray,
			categoryAxisConfig,
			valueAxisConfig,
			seriesConfigArray,
			themes,
			dataEntries,
			legend,
			cursor,
			shouldRotateLabels,
			componentHeight,
			horizontalScrollThreshold,
			supportDownload,
		} = config;

		if (modifiedData && !isEmpty(modifiedData)) {
			updateNoDataFlag(false);
			updateChartHeight(componentHeight);

			draw(
				modifiedData,
				barOrientation,
				tooltipHTMLArray,
				categoryAxisConfig,
				valueAxisConfig,
				seriesConfigArray,
				themes,
				dataEntries,
				legend,
				cursor,
				shouldRotateLabels,
				horizontalScrollThreshold,
				supportDownload
			);
		} else {
			updateNoDataFlag(true);
		}
	};

	const draw = (
		modifiedData: any,
		barOrientation: BarOrientation,
		tooltipHTMLArray: Array<string>,
		categoryAxisConfig: ICategoryAxisConfig,
		valueAxisConfig: IValueAxisConfig,
		seriesConfigArray: Array<ISeriesConfig>,
		themes: IChartThemes,
		dataEntries: Array<IDataEntries>,
		legend: IChartLegend,
		cursor: IChartCursor,
		shouldRotateLabels: boolean,
		horizontalScrollThreshold: number,
		supportDownload?: boolean
	) => {
		try {
			am4core.useTheme(am4themes_animated);

			if (_chartInstance) {
				_chartInstance.dispose();
			}

			// Create chart instance
			_chartInstance = am4core.create(chartId, am4charts.XYChart);
			_chartInstance.responsive.enabled = true;

			if (supportDownload) {
				addDownload(_chartInstance);
			}

			if (horizontalScrollThreshold) {
				const actualDataPoints = Array.isArray(modifiedData)
					? modifiedData.length
					: horizontalScrollThreshold;
				updateChartMinWidth(
					horizontalScrollThreshold > actualDataPoints
						? 100
						: (actualDataPoints / horizontalScrollThreshold) * 100
				);
			}

			_chartInstance.hiddenState.properties.opacity = 0;
			_chartInstance.paddingBottom = 0;
			_chartInstance.marginBottom = 0;

			_chartInstance.paddingLeft =
				barOrientation === BarOrientation.VERTICAL ? 0 : -10;
			_chartInstance.paddingRight = 0;

			// Add data
			_chartInstance.data = modifiedData;
			_chartInstance.zoomOutButton.disabled = true;

			if (cursor) {
				_chartInstance.cursor = new am4charts.XYCursor();
				_chartInstance.maskBullets = cursor.maskBullets;
			}

			const categoryAxis = createCategoryAxis(
				_chartInstance,
				themes,
				barOrientation,
				categoryAxisConfig,
				shouldRotateLabels
			);

			const valueAxis = createValueAxis(
				_chartInstance,
				themes,
				barOrientation,
				valueAxisConfig
			);

			if (valueAxisConfig.hide === true) {
				valueAxis.hide();
				valueAxis.renderer.labels.template.disabled = true;
			}

			if (categoryAxisConfig.hide === true) {
				categoryAxis.renderer.labels.template.disabled = true;
			}

			seriesConfigArray.forEach((seriesConfig, index) => {
				createSeries(
					_chartInstance,
					categoryAxis,
					themes.series[index],
					barOrientation,
					seriesConfig,
					dataEntries[index],
					tooltipHTMLArray[index]
				);
			});

			if (legend) {
				_chartInstance.legend = new am4charts.Legend();
				merge(_chartInstance.legend, legend);

				_chartInstance.legend.fontSize = 12;

				_chartInstance.legend.paddingBottom = 20;
				_chartInstance.legend.paddingLeft = 0;

				_chartInstance.legend.labels.template.maxWidth = 95;
				_chartInstance.legend.markers.template.width = 12;
				_chartInstance.legend.markers.template.height = 12;

				_chartInstance.legend.data = [];

				seriesConfigArray.forEach((seriesConfig, index) => {
					const fill = convertThemes(themes.series[index].color || '', "COLOR");
					_chartInstance.legend.data.push({name: seriesConfig.name, fill});

				});

				const marker = _chartInstance.legend.markers.template.children.getIndex(
					0
				);

				marker.cornerRadius(12, 12, 12, 12);

				_chartInstance.legend.position = "top";
				_chartInstance.legend.contentAlign = "left";

				_chartInstance.legend.itemContainers.template.clickable = false;

			}

			_chartInstance.logo.disabled = true;
		} catch (e) {
			console.error("COMMON XY CHART: Error in drawing chart", e);
			updateNoDataFlag(true);
		}
	};

	return (
		<div>
			{noDataFlag && <div className="empty-message">NO DATA</div>}
			{!noDataFlag && (
				<div style={{ overflowX: "auto" }}>
					<div
						id={chartId}
						style={{
							height: chartHeight,
							width: chartMinWidth + "%",
						}}
					></div>
				</div>
			)}
		</div>
	);
};

const createCategoryAxis = (
	_chartInstance: any,
	themes: IChartThemes,
	barOrientation: BarOrientation,
	axesConfig: ICategoryAxisConfig,
	shouldRotateLabels: boolean
) => {
	let axes;
	const categoryAxisObject = new am4charts.CategoryAxis();

	if (barOrientation === BarOrientation.HORIZONTAL) {
		axes = _chartInstance.yAxes.push(categoryAxisObject);
	} else {
		axes = _chartInstance.xAxes.push(categoryAxisObject);
	}
	axes.dataFields.category = axesConfig.dataKey;
	merge(axes.renderer, axesConfig);

	const templateFill = convertThemes(
		lodashGet(themes, ["categoryAxis", "labels", "template", "fill"], ""),
		"COLOR"
	);
	const templateFont = convertThemes(
		lodashGet(
			themes,
			["categoryAxis", "labels", "template", "fontSize"],
			""
		),
		"FONT"
	);

	const labelText = lodashGet(axesConfig, "label", null);

	if (labelText) {
		const titleFillColor = convertThemes(
			lodashGet(themes, ["valueAxis", "titleFill"]),
			"COLOR"
		);
		axes.title.text = labelText;
		axes.title.fill = am4core.color(titleFillColor);
		axes.title.fontSize = lodashGet(axesConfig, "labelFontSize");
	}

	axes.renderer.labels.template.fill = am4core.color(templateFill);
	axes.renderer.labels.template.fontSize = templateFont;
	axes.cursorTooltipEnabled = lodashGet(axesConfig, "cursorTooltipEnabled");

	if (shouldRotateLabels && barOrientation === BarOrientation.VERTICAL) {
		axes.events.on("sizechanged", function (ev: any) {
			const axis = ev.target;
			const cellWidth =
				axis.pixelWidth / (axis.endIndex - axis.startIndex);
			if (cellWidth < axis.renderer.labels.template.maxWidth) {
				axis.renderer.labels.template.rotation = -45;
				axis.renderer.labels.template.horizontalCenter = "right";
				axis.renderer.labels.template.verticalCenter = "middle";
			} else {
				axis.renderer.labels.template.rotation = 0;
				axis.renderer.labels.template.horizontalCenter = "middle";
				axis.renderer.labels.template.verticalCenter = "top";
			}
		});
	}

	return axes;
};

const createValueAxis = (
	_chartInstance: any,
	themes: IChartThemes,
	barOrientation: BarOrientation,
	axisConfig: IValueAxisConfig
) => {
	let axis;
	const valueAxisObject = new am4charts.ValueAxis();

	if (axisConfig.numberFormatter) {
		valueAxisObject.numberFormatter.numberFormat =
			axisConfig.numberFormatter;
	}

	if (barOrientation === BarOrientation.HORIZONTAL) {
		axis = _chartInstance.xAxes.push(valueAxisObject);
	} else {
		axis = _chartInstance.yAxes.push(valueAxisObject);
	}

	axis.renderer.grid.template.disabled = lodashGet(axisConfig, [
		"grid",
		"disabled",
	]);

	const fillColor = convertThemes(
		lodashGet(themes, ["valueAxis", "labels", "template", "fill"], ""),
		"COLOR"
	);
	const fontSize = convertThemes(
		lodashGet(
			themes,
			["valueAxis", "labels", "template", "fontSize"],
			"TEXT_14"
		),
		"FONT"
	);

	axis.renderer.labels.template.fill = am4core.color(fillColor);
	axis.renderer.labels.template.fontSize = fontSize;
	axis.min = lodashGet(axisConfig, "min");
	axis.extraMax = lodashGet(axisConfig, "extraMax");
	axis.maxPrecision = lodashGet(axisConfig, "maxPrecision");
	axis.cursorTooltipEnabled = lodashGet(axisConfig, "cursorTooltipEnabled");

	// this._Instance_value.renderer.labels.template.adapter.add(
	// 	'text',
	// 	function (text) {
	// 		return text + '%';
	// 	}
	// );

	const labelText = lodashGet(axisConfig, "label", null);

	if (labelText) {
		const titleFillColor = convertThemes(
			lodashGet(themes, ["valueAxis", "titleFill"]),
			"COLOR"
		);
		axis.title.text = labelText;
		axis.title.fill = am4core.color(titleFillColor);
		axis.title.fontSize = lodashGet(axisConfig, "labelFontSize");
	}

	return axis;
};

const addDownload = (chart: any) => {
	chart.exporting.menu = new am4core.ExportMenu();

	chart.exporting.menu.items = [
		{
			label: "...",
			menu: [{ type: "png", label: "PNG" }],
		},
	];
};

const createSeries = (
	_chartInstance: any,
	categoryAxis: any,
	theme: any,
	barOrientation: BarOrientation,
	seriesConfig: ISeriesConfig,
	dataEntry: IDataEntries,
	tooltipHTML: string
) => {
	const series = getSeries(_chartInstance, seriesConfig, theme, tooltipHTML);

	series.name = seriesConfig.name;
	series.stacked = seriesConfig.stacked;
	series.legendFillColor = convertThemes(theme.color, "COLOR");

	if (seriesConfig.chartType === XYChartType.COLUMN_SERIES) {
		categoryAxis.sortBySeries = series;
	}

	if (barOrientation === BarOrientation.HORIZONTAL) {
		series.dataFields.valueX = dataEntry.horizontalAxisDataKey;
		series.dataFields.categoryY = dataEntry.verticalAxisDataKey;
	} else {
		series.dataFields.categoryX = dataEntry.horizontalAxisDataKey;
		series.dataFields.valueY = dataEntry.verticalAxisDataKey;
	}

	const seriesTooltipConfig = lodashGet(seriesConfig, "tooltip", {});

	series.tooltip.label.interactionsEnabled = lodashGet(
		seriesTooltipConfig,
		["label", "interactionsEnabled"],
		false
	);
	series.tooltip.pointerOrientation = lodashGet(seriesTooltipConfig, "pointerOrientation");;
	series.tooltip.getFillFromObject = lodashGet(seriesTooltipConfig, "getFillFromObject");
	series.tooltip.background.fill = am4core.color(convertThemes(
		lodashGet(seriesTooltipConfig, ["background", "fill"]),
		"COLOR"
	));
	series.tooltip.label.fill = am4core.color(convertThemes(
		lodashGet(seriesTooltipConfig, ["label", "fill"]),
		"COLOR"
	));

	series.tooltip.background.fillOpacity = 1;
	series.tooltip.opacity = 1;

	const labelConfig = seriesConfig.labels;
	const valueLabel = series.bullets.push(new am4charts.LabelBullet());
	valueLabel.label.text = "{label}";
	merge(valueLabel.label, labelConfig);

	const valueLabelFontSize = convertThemes(
		lodashGet(theme, ["labels", "fontSize"]),
		"FONT"
	);
	const valueLabelFill = convertThemes(
		lodashGet(theme, ["labels", "fill"]),
		"COLOR"
	);

	valueLabel.label.fontSize = valueLabelFontSize;
	valueLabel.label.fill = am4core.color(valueLabelFill);
};

const getSeries = (
	_chartInstance: any,
	seriesConfig: ISeriesConfig,
	theme: any,
	tooltipHTML: string
) => {
	switch (seriesConfig.chartType) {
		case XYChartType.LINE_SERIES:
			return createLineSeries(_chartInstance, theme, tooltipHTML);
		case XYChartType.THRESHOLD:
			return createStepLineSeries(_chartInstance, seriesConfig, theme);
		default:
			return createColumnSeries(
				_chartInstance,
				seriesConfig,
				theme,
				tooltipHTML
			);
	}
};

const createColumnSeries = (
	_chartInstance: any,
	seriesConfig: ISeriesConfig,
	theme: any,
	tooltipHTML: string
) => {
	const series = _chartInstance.series.push(new am4charts.ColumnSeries());
	const seriesColumnWidth = lodashGet(seriesConfig, [
		"columns",
		"template",
		"width",
	]);

	const barStroke = am4core.color(convertThemes(
		lodashGet(theme, ["columns", "template", "stroke"]),
		"COLOR"
	));
	const barFill = am4core.color(convertThemes(
		lodashGet(theme, ["columns", "template", "fill"]),
		"COLOR"
	));
	series.columns.template.width = am4core.percent(seriesColumnWidth);
	series.columns.template.stroke = am4core.color(barStroke);
	series.columns.template.fill = am4core.color(barFill);
	series.columns.template.tooltipHTML = tooltipHTML;

	return series;
};

const createStepLineSeries = (
	_chartInstance: any,
	seriesConfig: ISeriesConfig,
	theme: any
) => {
	const series = _chartInstance.series.push(new am4charts.StepLineSeries());
	series.strokeWidth = 3;
	series.strokeDasharray = theme.strokeDasharray;
	series.stroke = am4core.color(convertThemes(theme.color, "COLOR"));
	series.noRisers = true;
	series.startLocation = seriesConfig.startLocation;
	series.endLocation = seriesConfig.endLocation;
	return series;
};

const createLineSeries = (
	_chartInstance: any,
	theme: any,
	tooltipHTML: string
) => {
	const series = _chartInstance.series.push(new am4charts.LineSeries());

	series.tooltip.pointerOrientation = "horizontal";
	series.tooltip.background.fill = am4core.color("#ffffff");
	series.tooltip.label.fill = am4core.color("#000000");
	series.tooltip.dy = -5;
	series.fill = am4core.color(convertThemes(theme.color, "COLOR"));
	series.fillOpacity = theme.fillOpacity || 0;
	series.strokeWidth = theme.stroke || 3;
	series.tensionX = 0.77;
	series.stroke = am4core.color(convertThemes(theme.color, "COLOR"));
	series.strokeDasharray = theme.strokeDasharray;

	const bulletConfig = lodashGet(theme, "bullet", null);
	if (bulletConfig) {
		const bullet = series.bullets.push(new am4charts.CircleBullet());
		bullet.circle.stroke = am4core.color(bulletConfig.stroke);
		bullet.circle.strokeWidth = bulletConfig.strokeWidth;
		bullet.strokeOpacity = 0;
		bullet.fillOpacity = 1; // make bullets always visible
		bullet.circle.fill = am4core.color(
			convertThemes(bulletConfig.fill, "COLOR")
		);
		bullet.circle.fillOpacity = 1;
		bullet.tooltipHTML = tooltipHTML;
	}

	return series;
};

const convertThemes = (theme: string, type: string) => {
	if (type === "COLOR") {
		return ChartColors[theme];
	} else {
		return ChartFonts[theme];
	}
};

export default XYChart;
